/**
 * Topic operations.
 *
 * Two resolvers live here, and they are **not** the same function. `findOrCreateTopic` skips
 * archived topics; `findOrCreateGenreTopics`, which came from the media hooks, does not. Unifying
 * them changes what media genre matching finds, so it is stated here rather than folded in silently
 * — `docs/target-structure.md` names it as one of the restructure's two behaviour changes and it
 * gets its own commit.
 */

import { newId } from '../ids'
import type { AppDocument, EntryType } from '../schema'

/**
 * The comparison key for a topic name: lowercase, alphanumerics only.
 *
 * Came from `utils/topic-utils.ts` on step 9 (#415), which `placement-drawers.md:64` sends here
 * rather than to `lib/` — the file imported `AppDocument`, and Rule L admits only `node_modules`
 * and `lib/`. Its two other exports came with no live caller and were deleted rather than moved:
 * `normalizeTopicNameCached` memoised this over a `Map` that nothing read, and `findSimilarTopic`
 * was `findOrCreateTopic`'s matching loop with the create half cut off.
 */
export function normalizeTopicName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .trim()
}

/**
 * The id of the topic with this name for this entry type, creating it if there is none.
 *
 * Matches on the normalised name and keeps the caller's spelling on a topic it creates. Returns
 * `null` for a blank name.
 */
export function findOrCreateTopic(
  doc: AppDocument,
  name: string,
  entryType: EntryType | null,
): string | null {
  const trimmed = name.trim()
  if (!trimmed) return null

  const normalized = normalizeTopicName(trimmed)

  const existing = Object.values(doc.topics).find((topic) => {
    if (topic.archivedAt) return false
    if (topic.entryType !== entryType) return false
    return normalizeTopicName(topic.name) === normalized
  })

  if (existing) return existing.id

  const id = newId()
  doc.topics[id] = {
    id,
    name: trimmed,
    description: '',
    parentId: null,
    entryType,
    isDefault: false,
  }

  return id
}

/**
 * Topic ids for a list of genre names, creating the ones that do not exist.
 *
 * The media path's resolver. It differs from `findOrCreateTopic` in two ways it inherited and keeps:
 * it does not skip archived topics, and it capitalises the first letter of a topic it creates.
 */
export function findOrCreateGenreTopics(
  doc: AppDocument,
  genres: readonly string[],
  scope: EntryType | 'album',
): string[] {
  return genres.reduce<string[]>((ids, genre) => {
    const trimmed = genre.trim()
    if (!trimmed) return ids

    const normalized = normalizeTopicName(trimmed)
    const existing = Object.values(doc.topics).find(
      (topic) =>
        topic.entryType === scope &&
        normalizeTopicName(topic.name) === normalized,
    )

    if (existing) {
      ids.push(existing.id)
      return ids
    }

    const id = newId()
    doc.topics[id] = {
      id,
      name: trimmed.charAt(0).toUpperCase() + trimmed.slice(1),
      description: '',
      parentId: null,
      entryType: scope as EntryType,
      isDefault: false,
    }
    ids.push(id)
    return ids
  }, [])
}
